'use client'

/**
 * Hoja de una carta/notita ya compuesta.
 *
 * Compartida por el generador de carta y la calculadora, que producen la misma
 * forma (`ComposedLetter`). Las acciones (guardar como imagen, imprimir) y el
 * canvas oculto viven en `PrintableSheet`, que es común a todos los imprimibles;
 * aquí solo queda cómo se ve la carta y cómo se pinta.
 */

import type { ComposedLetter } from '@/lib/guides/tools/tooth-letter'
import { drawLetter } from '@/lib/guides/tools/letter-canvas'
import { PRODUCT_EVENTS } from '@/lib/product-analytics'
import { PrintableSheet } from './printable-sheet'

export function ComposedLetterSheet({
  letter,
  sheetTitle,
  filename,
  analyticsProperties,
}: {
  letter: ComposedLetter
  sheetTitle: string
  filename: string
  /** Contexto del evento de guardado. Nunca datos del niño. */
  analyticsProperties: Record<string, unknown>
}) {
  return (
    <PrintableSheet
      className="rounded-2xl bg-[#fdf8ec] p-6 text-[#3f3222] shadow-xl md:p-8"
      draw={(canvas) => drawLetter(canvas, letter)}
      filename={filename}
      savedEvent={PRODUCT_EVENTS.GUIDE_LETTER_SAVED}
      analyticsProperties={analyticsProperties}
    >
      <figcaption className="mb-4 text-sm font-semibold uppercase tracking-wide text-[#3f3222]/60">
        {sheetTitle}
      </figcaption>
      <div className="space-y-4 font-serif text-lg leading-relaxed">
        <p className="text-xl font-bold">{letter.greeting}</p>
        {letter.paragraphs.map((paragraph) => (
          <p key={paragraph.slice(0, 40)}>{paragraph}</p>
        ))}
        <p>{letter.signoff}</p>
        <p className="text-right text-xl italic">{letter.signature}</p>
      </div>
    </PrintableSheet>
  )
}
